import React from 'react'
import { useTranslation } from 'react-i18next'
import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import ContactConciergerieButton from '~/components/ContactConciergerieLink'
import Container from '~/components/Container'
import Typography from '~/components/Typography'
import { matchIsMaintenanceMode } from '~/firebase.server'
import { useTrackPageView } from '~/hooks/useTrackPageView'
import { getFixedT } from '~/i18n/i18next.server'
import { buildMetas } from '~/utils/metas'
import ContactUsForm from '../ContactUsForm'
import brochureThumb from './brochure-thumb.png'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { t } = await getFixedT(request)
  const isMaintenanceMode = await matchIsMaintenanceMode(request)

  return {
    title: t('common:brochure'),
    isMaintenanceMode
  }
}

export const meta: MetaFunction<typeof loader> = ({
  location,
  params,
  data
}) => {
  return buildMetas({
    location,
    params,
    title: data?.title
  })
}

export const handle = {
  i18n: ['common', 'contact']
}

const ContactUsRoute = () => {
  const { t } = useTranslation(['common', 'contact'])
  const { isMaintenanceMode } = useLoaderData<typeof loader>()
  useTrackPageView('Contact us Brochure')

  return (
    <Container className="flex gap-20 pb-32 pt-16 max-lg:flex-col">
      <div className="flex flex-none flex-col gap-y-6">
        <Typography variant="h1" family="brand" className="max-lg:text-center">
          {t('common:brochure')}
        </Typography>
        <div className="flex max-w-lg flex-col gap-y-10 max-lg:mx-auto">
          <div className="flex max-w-md flex-col gap-y-3">
            <Typography
              color="shy"
              variant="body1"
              className="max-lg:text-center"
            >
              {t('contact:brochureDescription')}
            </Typography>
            <Typography
              color="shy"
              variant="body2"
              className="max-lg:text-center"
            >
              {t('contact:brochureInstructions')}
            </Typography>
          </div>
          <img
            src={brochureThumb}
            alt={t('contact:brochure')}
            loading="lazy"
            className="mx-auto"
            width={240}
          />
        </div>
      </div>
      {isMaintenanceMode ? (
        <div className="flex flex-col gap-y-6">
          <Typography className="text-center" variant="h5">
            {t('common:formUnavailable')}
          </Typography>
          <ContactConciergerieButton />
        </div>
      ) : (
        <ContactUsForm isBrochureRequested />
      )}
    </Container>
  )
}

export default ContactUsRoute
